import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Replace, Tag, Edit, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { KnowledgeDocument } from '../types';
import { useAiManagement } from '@/hooks/use-ai-management';

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

interface BulkEditOperation {
  type: 'find_replace' | 'tag_update' | 'metadata_update';
  field: string;
  findValue: string;
  replaceValue: string;
  operation?: 'add' | 'remove' | 'replace';
}

interface PreviewChange {
  documentId: string;
  field: string;
  oldValue: string;
  newValue: string;
}

export const BulkEditDialog: React.FC<BulkEditDialogProps> = ({
  open,
  onOpenChange,
  onComplete
}) => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('select');
  const [operations, setOperations] = useState<BulkEditOperation[]>([]);
  const [previewChanges, setPreviewChanges] = useState<PreviewChange[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [useRegex, setUseRegex] = useState(false);
  
  const { generateEmbeddingsBatch } = useAiManagement();

  // Fetch documents
  useEffect(() => {
    if (open) {
      fetchDocuments();
    }
  }, [open]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('assistant_knowledge')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  // Filter documents based on search
  const filteredDocuments = documents.filter(doc =>
    doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.document?.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.metadata?.tags?.some((tag: string) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Add operation
  const addOperation = () => {
    setOperations([
      ...operations,
      {
        type: 'find_replace',
        field: 'content',
        findValue: '',
        replaceValue: '',
        operation: 'replace'
      }
    ]);
  };

  // Remove operation
  const removeOperation = (index: number) => {
    setOperations(operations.filter((_, i) => i !== index));
  };

  // Update operation
  const updateOperation = (index: number, updates: Partial<BulkEditOperation>) => {
    const newOperations = [...operations];
    newOperations[index] = { ...newOperations[index], ...updates };
    setOperations(newOperations);
  };

  // Preview changes function - renamed to avoid conflict
  const generatePreview = async () => {
    if (selectedDocs.size === 0 || operations.length === 0) {
      toast.error('Please select documents and add operations');
      return;
    }

    const changes: PreviewChange[] = [];
    const selectedDocuments = documents.filter(doc => selectedDocs.has(doc.id));

    for (const doc of selectedDocuments) {
      for (const operation of operations) {
        let oldValue = '';
        let newValue = '';

        if (operation.type === 'find_replace') {
          if (operation.field === 'content') {
            oldValue = doc.document?.content || '';
          } else if (operation.field === 'title') {
            oldValue = doc.title || '';
          }

          if (oldValue) {
            if (useRegex) {
              try {
                const regex = new RegExp(operation.findValue, 'g');
                newValue = oldValue.replace(regex, operation.replaceValue);
              } catch (e) {
                newValue = oldValue;
              }
            } else {
              newValue = oldValue.replace(new RegExp(operation.findValue, 'g'), operation.replaceValue);
            }

            if (oldValue !== newValue) {
              changes.push({
                documentId: doc.id,
                field: operation.field,
                oldValue: oldValue.substring(0, 100) + (oldValue.length > 100 ? '...' : ''),
                newValue: newValue.substring(0, 100) + (newValue.length > 100 ? '...' : '')
              });
            }
          }
        } else if (operation.type === 'tag_update') {
          const currentTags = doc.metadata?.tags || [];
          let newTags = [...currentTags];

          if (operation.operation === 'add' && !currentTags.includes(operation.replaceValue)) {
            newTags.push(operation.replaceValue);
          } else if (operation.operation === 'remove') {
            newTags = newTags.filter(tag => tag !== operation.findValue);
          } else if (operation.operation === 'replace') {
            newTags = newTags.map(tag => tag === operation.findValue ? operation.replaceValue : tag);
          }

          if (JSON.stringify(currentTags) !== JSON.stringify(newTags)) {
            changes.push({
              documentId: doc.id,
              field: 'tags',
              oldValue: currentTags.join(', '),
              newValue: newTags.join(', ')
            });
          }
        }
      }
    }

    setPreviewChanges(changes);
    setActiveTab('preview');
  };

  // Apply changes
  const applyChanges = async () => {
    if (selectedDocs.size === 0 || operations.length === 0) {
      toast.error('Please select documents and add operations');
      return;
    }

    setProcessing(true);
    setProgress(0);

    try {
      const selectedDocuments = documents.filter(doc => selectedDocs.has(doc.id));
      const total = selectedDocuments.length;
      let completed = 0;

      for (const doc of selectedDocuments) {
        let needsEmbeddingUpdate = false;
        let updatedDoc = { ...doc };

        // Apply all operations to this document
        for (const operation of operations) {
          if (operation.type === 'find_replace') {
            if (operation.field === 'content') {
              const oldContent = updatedDoc.document?.content || '';
              let newContent = oldContent;

              if (useRegex) {
                try {
                  const regex = new RegExp(operation.findValue, 'g');
                  newContent = oldContent.replace(regex, operation.replaceValue);
                } catch (e) {
                  console.error('Invalid regex:', operation.findValue);
                }
              } else {
                newContent = oldContent.replace(new RegExp(operation.findValue, 'g'), operation.replaceValue);
              }

              if (oldContent !== newContent) {
                updatedDoc.document = { ...updatedDoc.document, content: newContent };
                needsEmbeddingUpdate = true;
              }
            } else if (operation.field === 'title') {
              const oldTitle = updatedDoc.title || '';
              let newTitle = oldTitle;

              if (useRegex) {
                try {
                  const regex = new RegExp(operation.findValue, 'g');
                  newTitle = oldTitle.replace(regex, operation.replaceValue);
                } catch (e) {
                  console.error('Invalid regex:', operation.findValue);
                }
              } else {
                newTitle = oldTitle.replace(new RegExp(operation.findValue, 'g'), operation.replaceValue);
              }

              if (oldTitle !== newTitle) {
                updatedDoc.title = newTitle;
                needsEmbeddingUpdate = true;
              }
            }
          } else if (operation.type === 'tag_update') {
            const currentTags = updatedDoc.metadata?.tags || [];
            let newTags = [...currentTags];

            if (operation.operation === 'add' && !currentTags.includes(operation.replaceValue)) {
              newTags.push(operation.replaceValue);
            } else if (operation.operation === 'remove') {
              newTags = newTags.filter(tag => tag !== operation.findValue);
            } else if (operation.operation === 'replace') {
              newTags = newTags.map(tag => tag === operation.findValue ? operation.replaceValue : tag);
            }

            updatedDoc.metadata = {
              ...updatedDoc.metadata,
              tags: newTags
            };
          } else if (operation.type === 'metadata_update') {
            updatedDoc.metadata = {
              ...updatedDoc.metadata,
              [operation.field]: operation.replaceValue
            };
          }
        }

        // Generate new embedding if content changed
        let newEmbedding = null;
        if (needsEmbeddingUpdate) {
          const text = `${updatedDoc.title}\n\n${updatedDoc.document?.content}`;
          const embeddings = await generateEmbeddingsBatch([text], {
            concurrency: 1,
            onProgress: (current, total) => {
              // Update progress within this document
            }
          });
          newEmbedding = embeddings[0];
        }

        // Update document in database
        const updateData: any = {
          title: updatedDoc.title,
          document: updatedDoc.document,
          metadata: updatedDoc.metadata
        };

        if (newEmbedding) {
          updateData.vector = newEmbedding;
        }

        const { error } = await supabase
          .from('assistant_knowledge')
          .update(updateData)
          .eq('id', doc.id);

        if (error) {
          throw error;
        }

        completed++;
        setProgress((completed / total) * 100);
      }

      toast.success(`Successfully updated ${completed} documents`);
      onComplete();
      onOpenChange(false);
    } catch (error) {
      console.error('Error applying bulk changes:', error);
      toast.error('Failed to apply changes');
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  // Toggle document selection
  const toggleDocument = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  // Select all filtered documents
  const selectAll = () => {
    const allIds = new Set(filteredDocuments.map(doc => doc.id));
    setSelectedDocs(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedDocs(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden w-full">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl">Bulk Edit Documents</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="select" className="text-sm">Select Documents</TabsTrigger>
              <TabsTrigger value="operations" className="text-sm">Edit Operations</TabsTrigger>
              <TabsTrigger value="preview" className="text-sm">Preview Changes</TabsTrigger>
              <TabsTrigger value="apply" className="text-sm">Apply Changes</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="select" className="space-y-6 h-full overflow-hidden">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <Search className="h-5 w-5" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-80 h-10"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <Button variant="outline" size="default" onClick={selectAll}>
                      Select All ({filteredDocuments.length})
                    </Button>
                    <Button variant="outline" size="default" onClick={clearSelection}>
                      Clear Selection
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  Selected: <span className="font-semibold">{selectedDocs.size}</span> documents
                </div>

                <div className="flex-1 overflow-hidden border rounded-lg">
                  <div className="h-[60vh] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="w-16">Select</TableHead>
                          <TableHead className="min-w-[250px]">Title</TableHead>
                          <TableHead className="w-32">Topic</TableHead>
                          <TableHead className="w-32">Language</TableHead>
                          <TableHead className="min-w-[200px]">Tags</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDocuments.map((doc) => (
                          <TableRow key={doc.id} className="hover:bg-muted/50">
                            <TableCell>
                              <Checkbox
                                checked={selectedDocs.has(doc.id)}
                                onCheckedChange={() => toggleDocument(doc.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium max-w-[300px]">
                              <div className="truncate" title={doc.title}>
                                {doc.title}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{doc.metadata?.topic || 'guide'}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{doc.metadata?.language || 'en'}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {doc.metadata?.tags?.slice(0, 4).map((tag: string) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {doc.metadata?.tags && doc.metadata.tags.length > 4 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{doc.metadata.tags.length - 4}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="operations" className="space-y-6 h-full overflow-hidden">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Edit Operations</h3>
                  <div className="flex items-center space-x-3">
                    <Switch
                      id="use-regex"
                      checked={useRegex}
                      onCheckedChange={setUseRegex}
                    />
                    <Label htmlFor="use-regex" className="text-sm font-medium">Use Regex</Label>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-6 max-h-[65vh]">
                  {operations.map((operation, index) => (
                    <div key={index} className="border rounded-lg p-6 space-y-6 bg-card">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium text-lg">Operation {index + 1}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOperation(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Operation Type</Label>
                          <Select
                            value={operation.type}
                            onValueChange={(value: any) => updateOperation(index, { type: value })}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="find_replace">Find & Replace</SelectItem>
                              <SelectItem value="tag_update">Tag Update</SelectItem>
                              <SelectItem value="metadata_update">Metadata Update</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {operation.type === 'find_replace' && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Field</Label>
                            <Select
                              value={operation.field}
                              onValueChange={(value) => updateOperation(index, { field: value })}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="content">Content</SelectItem>
                                <SelectItem value="title">Title</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {operation.type === 'tag_update' && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Tag Operation</Label>
                            <Select
                              value={operation.operation}
                              onValueChange={(value: any) => updateOperation(index, { operation: value })}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="add">Add Tag</SelectItem>
                                <SelectItem value="remove">Remove Tag</SelectItem>
                                <SelectItem value="replace">Replace Tag</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {operation.type === 'metadata_update' && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Metadata Field</Label>
                            <Select
                              value={operation.field}
                              onValueChange={(value) => updateOperation(index, { field: value })}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="topic">Topic</SelectItem>
                                <SelectItem value="language">Language</SelectItem>
                                <SelectItem value="source_language">Source Language</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            {operation.type === 'find_replace' ? 'Find' : 
                             operation.type === 'tag_update' && operation.operation !== 'add' ? 'Tag to find' : 
                             'Current value'}
                          </Label>
                          <Input
                            value={operation.findValue}
                            onChange={(e) => updateOperation(index, { findValue: e.target.value })}
                            placeholder={operation.type === 'find_replace' ? 'Text to find' : 'Value to find'}
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            {operation.type === 'find_replace' ? 'Replace with' : 
                             operation.type === 'tag_update' ? 'New tag' : 
                             'New value'}
                          </Label>
                          <Input
                            value={operation.replaceValue}
                            onChange={(e) => updateOperation(index, { replaceValue: e.target.value })}
                            placeholder={operation.type === 'find_replace' ? 'Replacement text' : 'New value'}
                            className="h-10"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button onClick={addOperation} variant="outline" className="w-full h-12">
                    <Edit className="h-4 w-4 mr-2" />
                    Add Operation
                  </Button>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button 
                    onClick={generatePreview} 
                    disabled={selectedDocs.size === 0 || operations.length === 0}
                    size="lg"
                  >
                    Preview Changes
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="preview" className="space-y-6 h-full overflow-hidden">
                <h3 className="text-lg font-semibold">Preview Changes</h3>
                
                {previewChanges.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-lg">No changes to preview</p>
                    <p className="text-sm">Make sure you have selected documents and added operations</p>
                  </div>
                ) : (
                  <div className="space-y-6 h-full flex flex-col">
                    <div className="bg-muted/30 p-4 rounded-lg">
                      <p className="text-sm font-medium">
                        {previewChanges.length} changes will be applied to {selectedDocs.size} documents
                      </p>
                    </div>

                    <div className="flex-1 overflow-hidden border rounded-lg">
                      <div className="h-[55vh] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead className="min-w-[200px]">Document</TableHead>
                              <TableHead className="w-32">Field</TableHead>
                              <TableHead className="min-w-[250px]">Old Value</TableHead>
                              <TableHead className="min-w-[250px]">New Value</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewChanges.map((change, index) => {
                              const doc = documents.find(d => d.id === change.documentId);
                              return (
                                <TableRow key={index} className="hover:bg-muted/50">
                                  <TableCell className="font-medium">
                                    <div className="truncate max-w-[200px]" title={doc?.title}>
                                      {doc?.title}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="capitalize">
                                      {change.field}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="max-w-[250px]">
                                    <div className="truncate text-sm" title={change.oldValue}>
                                      {change.oldValue}
                                    </div>
                                  </TableCell>
                                  <TableCell className="max-w-[250px]">
                                    <div className="truncate text-sm font-medium text-green-700" title={change.newValue}>
                                      {change.newValue}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                      <Button onClick={() => setActiveTab('apply')} size="lg">
                        Continue to Apply
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="apply" className="space-y-6 h-full">
                <h3 className="text-lg font-semibold">Apply Changes</h3>
                
                {!processing ? (
                  <div className="space-y-6">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        <span className="font-medium text-yellow-800 text-lg">Warning</span>
                      </div>
                      <p className="text-yellow-700">
                        This will apply <span className="font-semibold">{previewChanges.length} changes</span> to <span className="font-semibold">{selectedDocs.size} documents</span>. 
                        Content changes will trigger re-embedding. This action cannot be undone.
                      </p>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <Button variant="outline" onClick={() => setActiveTab('preview')} size="lg">
                        Back to Preview
                      </Button>
                      <Button onClick={applyChanges} className="bg-red-600 hover:bg-red-700" size="lg">
                        Apply Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 py-12">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-8 h-8 text-primary animate-pulse" />
                      </div>
                      <h4 className="font-medium text-lg mb-2">Applying Changes...</h4>
                      <p className="text-muted-foreground">
                        Processing documents and updating embeddings
                      </p>
                    </div>
                    
                    <div className="max-w-md mx-auto space-y-3">
                      <Progress value={progress} className="h-3" />
                      <div className="text-center text-sm text-muted-foreground">
                        {Math.round(progress)}% complete
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};
